import { defineStore } from 'pinia';
import dayjs from 'dayjs';
import { TempRecordType, TempRecordItem, TempRecordState, TEMP_RECORD_KEYS } from './types';
import { useUserStore } from '@/store/user';
import {
  TEMP_RECORD_PERSIST_KEY,
  onTempRecordStorageSync,
  readPersistedTempRecords,
  tryClaimTempRecordInStorage,
} from './crossTabClaim';

let storageSyncBound = false;

/**
 * 临时记录Store
 */
export const useTempRecordStore = defineStore('tempRecord', {
  state: (): TempRecordState => ({
    records: {}
  }),

  getters: {
    /**
     * 获取当前用户的记录
     */
    currentUserRecords(): Record<string, TempRecordItem> {
      const userStore = useUserStore();
      const userId = userStore.userInfo.uniqueId || 'anonymous';
      return this.records[userId] || {};
    },

    /**
     * 获取当前用户有效的记录（过滤掉过期的记录）
     */
    validRecords(): Record<string, TempRecordItem> {
      const now = dayjs();
      const valid: Record<string, TempRecordItem> = {};
      const currentRecords = this.currentUserRecords;
      
      Object.entries(currentRecords).forEach(([key, item]) => {
        if (item.type === TempRecordType.DAILY_RESET && item.expireDate) {
          // 检查每日重置类型是否过期
          if (now.format('YYYY-MM-DD') <= item.expireDate) {
            valid[key] = item;
          }
        } else {
          // TOKEN_CYCLE 和 GLOBAL_ONCE 类型不需要检查过期
          valid[key] = item;
        }
      });
      
      return valid;
    }
  },

  actions: {
    /**
     * 获取当前用户ID
     */
    getCurrentUserId(): string {
      const userStore = useUserStore();
      return userStore.userInfo.uniqueId || 'anonymous';
    },

    /**
     * 确保当前用户的记录对象存在
     */
    ensureUserRecords(userId?: string): void {
      const currentUserId = userId || this.getCurrentUserId();
      if (!this.records[currentUserId]) {
        this.records[currentUserId] = {};
      }
    },

    /**
     * 设置临时记录
     * @param key 记录键名
     * @param value 记录值
     * @param type 记录类型
     */
    setRecord(key: string, value: any, type: TempRecordType = TempRecordType.TOKEN_CYCLE) {
      const userId = this.getCurrentUserId();
      this.ensureUserRecords(userId);

      const item: TempRecordItem = {
        value,
        type,
        timestamp: Date.now()
      };

      // 如果是每日重置类型，设置过期日期为当天
      if (type === TempRecordType.DAILY_RESET) {
        item.expireDate = dayjs().format('YYYY-MM-DD');
      }

      this.records[userId][key] = item;
    },

    /**
     * 获取临时记录
     * @param key 记录键名
     * @param defaultValue 默认值
     * @returns 记录值或默认值
     */
    getRecord<T = any>(key: string, defaultValue?: T): T | undefined {
      const validRecords = this.validRecords;
      const item = validRecords[key];
      
      if (!item) {
        return defaultValue;
      }

      return item.value as T;
    },

    /**
     * 检查记录是否存在且有效
     * @param key 记录键名
     * @returns 是否存在
     */
    hasRecord(key: string): boolean {
      return key in this.validRecords;
    },

    /**
     * 从 localStorage 拉取最新 records，覆盖内存（跨 Tab 同步）
     */
    syncFromStorage() {
      this.records = readPersistedTempRecords();
    },

    /**
     * 跨 Tab 原子占坑：成功返回 true（调用方才应继续展示）；失败表示其它 Tab 已占过。
     * 结束后把内存同步到 localStorage 最新态（含失败时同步其它 Tab 的占坑结果）。
     */
    async tryClaimRecord(
      key: string,
      value: any,
      type: TempRecordType = TempRecordType.TOKEN_CYCLE,
    ): Promise<boolean> {
      const userId = this.getCurrentUserId();
      const claimed = await tryClaimTempRecordInStorage({ userId, key, value, type });
      this.syncFromStorage();
      return claimed;
    },

    /**
     * 删除记录
     * @param key 记录键名
     */
    removeRecord(key: string) {
      const userId = this.getCurrentUserId();
      if (this.records[userId]) {
        delete this.records[userId][key];
      }
    },

    /**
     * 清除指定类型的记录
     * @param type 记录类型
     */
    clearRecordsByType(type: TempRecordType) {
      const userId = this.getCurrentUserId();
      const userRecords = this.records[userId];
      
      if (userRecords) {
        Object.keys(userRecords).forEach(key => {
          if (userRecords[key].type === type) {
            delete userRecords[key];
          }
        });
      }
    },

    /**
     * 清除token周期记录（用于登录/退出时调用）
     */
    clearTokenCycleRecords() {
      this.clearRecordsByType(TempRecordType.TOKEN_CYCLE);
    },

    /**
     * 清除过期的每日重置记录
     */
    clearExpiredDailyRecords() {
      const now = dayjs();
      const userId = this.getCurrentUserId();
      const userRecords = this.records[userId];
      
      if (userRecords) {
        Object.keys(userRecords).forEach(key => {
          const item = userRecords[key];
          if (item.type === TempRecordType.DAILY_RESET && item.expireDate) {
            if (now.format('YYYY-MM-DD') > item.expireDate) {
              delete userRecords[key];
            }
          }
        });
      }
    },

    /**
     * 清除当前用户的所有记录
     */
    clearAllRecords() {
      const userId = this.getCurrentUserId();
      this.records[userId] = {};
    },

    /**
     * 清除所有用户的所有记录
     */
    clearAllUsersRecords() {
      this.records = {};
    },

    /**
     * 初始化清理（应用启动时调用）
     */
    initialize() {
      // 清理过期的每日重置记录
      this.clearExpiredDailyRecords();
      // 其它 Tab 写入 localStorage 时同步内存，避免误判「今日未弹」
      if (!storageSyncBound) {
        storageSyncBound = true;
        onTempRecordStorageSync((records) => {
          this.records = records;
        });
      }
    },

    /**
     * 用户登录时调用 - 清除token周期数据，开始新的token周期
     */
    onUserLogin() {
      this.clearTokenCycleRecords();
      // 清理过期的每日重置记录
      this.clearExpiredDailyRecords();
    },

    /**
     * 用户退出时调用 - 清除token周期数据
     */
    onUserLogout() {
      this.clearTokenCycleRecords();
    },

    /**
     * 用户切换时调用 - 清理前一个用户的token周期数据
     * @param previousUserId 前一个用户ID
     */
    onUserSwitch(previousUserId?: string) {
      if (previousUserId && this.records[previousUserId]) {
        // 清理前一个用户的token周期记录
        const previousUserRecords = this.records[previousUserId];
        Object.keys(previousUserRecords).forEach(key => {
          if (previousUserRecords[key].type === TempRecordType.TOKEN_CYCLE) {
            delete previousUserRecords[key];
          }
        });
      }
      
      // 清理当前用户的过期记录
      this.clearExpiredDailyRecords();
    }
  },

  persist: {
    key: TEMP_RECORD_PERSIST_KEY,
    storage: window.localStorage,
    paths: ['records']
  }
});

// 重新导出类型和常量
export { TempRecordType, TEMP_RECORD_KEYS } from './types';